import type { CardInstance, Combat, PlayTarget } from '../core/combat';
import { canPlay, freeSwap, playCard } from '../core/combat';
import { idxCol, idxRow, type CellIndex, type GemKind } from '../core/types';
import { cellAt, columnAt, contains, type Layout } from '../render/layout';

export type AimMode = 'idle' | 'piece' | 'swap' | 'cell' | 'area' | 'column' | 'color';

export interface Aim {
  readonly mode: AimMode;
  readonly card: CardInstance | null;
  readonly column: number;
  readonly rotation: number;
  /** First cell picked, for swap targeting. */
  readonly first: CellIndex | null;
}

export interface Hover {
  readonly cell: CellIndex | null;
  readonly column: number | null;
  readonly cardIndex: number | null;
  readonly enemyIndex: number | null;
  readonly overEndTurn: boolean;
  readonly overRotate: boolean;
  readonly overCancel: boolean;
  readonly color: GemKind | null;
}

export interface Controller {
  aim: Aim;
  hover: Hover;
}

const IDLE_AIM: Aim = { mode: 'idle', card: null, column: 3, rotation: 0, first: null };

const IDLE_HOVER: Hover = {
  cell: null,
  column: null,
  cardIndex: null,
  enemyIndex: null,
  overEndTurn: false,
  overRotate: false,
  overCancel: false,
  color: null,
};

export function createController(): Controller {
  return { aim: { ...IDLE_AIM }, hover: { ...IDLE_HOVER } };
}

export function isAiming(c: Controller): boolean {
  return c.aim.mode !== 'idle';
}

export function cancelAim(c: Controller): void {
  c.aim = { ...IDLE_AIM };
}

function modeFor(card: CardInstance): AimMode {
  const t = card.def.target;
  if (t === 'none') return 'idle';
  return t;
}

/** Enter aim mode for a card. Cards that need no targeting return false. */
export function beginAim(c: Controller, combat: Combat, card: CardInstance): boolean {
  if (!canPlay(combat, card)) return false;
  const mode = modeFor(card);
  if (mode === 'idle') return false;
  c.aim = { mode, card, column: c.aim.column, rotation: 0, first: null };
  return true;
}

export function rotateAim(c: Controller): void {
  if (c.aim.mode !== 'piece') return;
  c.aim = { ...c.aim, rotation: (c.aim.rotation + 1) % 4 };
}

/** Arm a free swap with the first gem already chosen. */
export function beginFreeSwap(c: Controller, cell: CellIndex): void {
  c.aim = { mode: 'swap', card: null, column: c.aim.column, rotation: 0, first: cell };
}

export function setAimColumn(c: Controller, column: number): void {
  c.aim = { ...c.aim, column };
}

// --- hover ----------------------------------------------------------------

export function updateHover(c: Controller, l: Layout, combat: Combat, x: number, y: number): void {
  let cardIndex: number | null = null;
  for (let i = 0; i < l.cards.length; i++) {
    const r = l.cards[i];
    if (r && contains(r, x, y)) cardIndex = i;
  }

  let enemyIndex: number | null = null;
  for (let i = 0; i < combat.enemies.length; i++) {
    const r = l.enemies[i];
    if (r && contains(r, x, y)) enemyIndex = i;
  }

  const cell = cellAt(l, x, y);
  const column = columnAt(l, x, y);
  let color: GemKind | null = null;
  if (cell !== null) color = combat.grid[cell] ?? null;

  c.hover = {
    cell,
    column,
    cardIndex,
    enemyIndex,
    overEndTurn: contains(l.endTurn, x, y),
    overRotate: contains(l.rotate, x, y),
    overCancel: contains(l.cancel, x, y),
    color,
  };

  // A held piece tracks the column under the pointer, so the ghost follows the
  // mouse without a second call site having to remember to sync it.
  if (c.aim.mode === 'piece' && column !== null) {
    c.aim = { ...c.aim, column };
  }
}

// --- activation -----------------------------------------------------------

export function aimTarget(aim: Aim, hover: Hover): PlayTarget | null {
  switch (aim.mode) {
    case 'piece':
      return { column: aim.column, rotation: aim.rotation };
    case 'cell':
    case 'area':
      return hover.cell === null ? null : { cell: hover.cell };
    case 'column':
      return hover.column === null ? null : { column: hover.column };
    case 'color':
      return hover.color === null ? null : { color: hover.color };
    case 'swap': {
      if (hover.cell === null || aim.first === null) return null;
      return { cell: aim.first, swapWith: hover.cell };
    }
    case 'idle':
      return null;
  }
}

/**
 * Resolve a click against the current aim. Returns true when the click was
 * consumed — either it played a card or it advanced a two-step target.
 */
export function pointerDown(c: Controller, combat: Combat, x: number, y: number): boolean {
  const { aim, hover } = c;

  // Swap is two clicks: pick, then confirm an orthogonal neighbour. With no
  // card attached this is the free once-per-turn swap.
  if (aim.mode === 'swap') {
    if (aim.first === null) {
      if (hover.cell === null) return false;
      c.aim = { ...aim, first: hover.cell };
      return true;
    }
    if (hover.cell === aim.first) {
      c.aim = { ...aim, first: null };
      return true;
    }
    const target = aimTarget(aim, hover);
    const first = aim.first;
    if (!target || target.swapWith === undefined) {
      c.aim = { ...aim, first: null };
      return true;
    }
    const card = aim.card;
    cancelAim(c);
    if (card) playCard(combat, card, target);
    else freeSwap(combat, first, target.swapWith);
    return true;
  }

  if (aim.mode === 'idle') return false;

  // Piece aiming keys off the column the pointer is in, not a cell hit, so the
  // well's edge columns stay reachable.
  if (aim.mode === 'piece' && hover.column !== null) {
    c.aim = { ...aim, column: hover.column };
  }

  const target = aimTarget(c.aim, hover);
  if (!target) return false;

  const card = aim.card;
  cancelAim(c);
  if (card) playCard(combat, card, target);
  return true;
}

/**
 * Click on a hand card. No-target cards fire immediately; the rest enter aim.
 */
export function clickCard(c: Controller, combat: Combat, index: number): boolean {
  const card = combat.hand[index];
  if (!card || !canPlay(combat, card)) return false;
  if (card.def.target === 'none') {
    cancelAim(c);
    playCard(combat, card, {});
    return true;
  }
  return beginAim(c, combat, card);
}

export function handIndexAt(l: Layout, x: number, y: number): number | null {
  for (let i = l.cards.length - 1; i >= 0; i--) {
    const r = l.cards[i];
    // Topmost drawn card wins the hit when the spread overlaps.
    if (r && x >= r.x && x < r.x + r.w && y >= r.y - 24 && y < r.y + r.h) return i;
  }
  return null;
}

export function adjacent(a: CellIndex, b: CellIndex): boolean {
  return Math.abs(idxRow(a) - idxRow(b)) + Math.abs(idxCol(a) - idxCol(b)) === 1;
}
